import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Crown, PlusCircle, Trash2, Eye, EyeOff, Tag, Globe, Store, BellRing, Zap, Phone, MessageCircle, Loader2, Image as ImageIcon, PiggyBank, Sparkles, Award, Lock, QrCode, Camera, X, Flame, Target, Mail, Copy, CheckCircle2, Navigation, CalendarClock, RefreshCw, LogOut, ChevronRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useSubscriptions } from "../context/SubscriptionsContext";
import { useToast } from "../hooks/use-toast";
import { ToastAction } from "../components/ui/toast";
import { useFx } from "../context/FxContext";
import { symbolFor, CURRENCIES } from "../utils/currency";
import { compressImage, thumb } from "../utils/image";
import { startCheckout } from "../utils/checkout";
import { ShareSavings } from "../components/dashboard/ShareSavings";
import { TwoFactorCard } from "../components/dashboard/TwoFactorCard";
import { AccountSecurityCard } from "../components/dashboard/AccountSecurityCard";
import { SessionsCard } from "../components/dashboard/SessionsCard";
import { DeleteAccountCard } from "../components/dashboard/DeleteAccountCard";
import { MyMessagesCard } from "../components/dashboard/MyMessagesCard";
import { EmailFrequencyCard } from "../components/dashboard/EmailFrequencyCard";
import { SecurityNudge } from "../components/dashboard/SecurityNudge";
import { ShopPromoCard } from "../components/dashboard/ShopPromoCard";
import { PasskeysCard } from "../components/dashboard/PasskeysCard";
import { ChainEnquiry } from "../components/dashboard/ChainEnquiry";
import { PlanCard } from "../components/dashboard/PlanCard";
import { BillingHistory } from "../components/dashboard/BillingHistory";
import { RedemptionGoalCard } from "../components/dashboard/RedemptionGoalCard";
import { OpeningHoursCard } from "../components/dashboard/OpeningHoursCard";
import { BranchesCard } from "../components/dashboard/BranchesCard";
import { useLang } from "../context/LanguageContext";
import { VerificationCard } from "../components/dashboard/VerificationCard";
import { SlotRadar } from "../components/dashboard/SlotRadar";
import { ShopLinkCard } from "../components/dashboard/ShopLinkCard";
import { FeaturedPreview } from "../components/dashboard/FeaturedPreview";
import { AppealsCard } from "../components/dashboard/AppealsCard";
import { BoostOptions } from "../components/dashboard/BoostOptions";

const Account = () => {
  const { t } = useLang();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { displayCurrency, setDisplayCurrency, rates } = useFx();
  const { subs, toggleFollow } = useSubscriptions();
  const [deals, setDeals] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contact, setContact] = useState({ phone: "", whatsapp: "", contacts: [], contactsPublic: true });
  const weeklyDigest = user?.emailPrefs?.weeklyDigest !== false;
  const saveDigest = async () => {
    try {
      await api.put("/my/email-prefs", { weeklyDigest: !weeklyDigest });
      await refresh();
      toast({ title: !weeklyDigest ? t("acc.digestOn") : t("acc.digestOff"),
              description: !weeklyDigest ? t("acc.digestOnNote") : t("acc.digestOffNote") });
    } catch { toast({ title: t("acc.couldNotUpdatePreferences") }); }
  };
  const [savings, setSavings] = useState(null);
  const [credits, setCredits] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemResult, setRedeemResult] = useState(null);
  const [redeemSummary, setRedeemSummary] = useState(null);
  const [myCodes, setMyCodes] = useState([]);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  // The shop account had grown into one endless scroll — each area is its own section now.
  const [section, setSection] = useState(() => (window.location.hash === "#verify" ? "account" : "deals"));
  const SECTIONS = [
    ["deals", t("acc.tabDeals")],
    ["shop", t("acc.tabShop")],
    ["redeem", t("acc.tabRedeem")],
    ["account", t("acc.tabAccount")],
  ];

  const isShopper = user?.role === "shopper";
  const SAVINGS_ENABLED = true; // Counts ONLY verified in-store QR redemptions
  const load = () => api.get("/my/deals").then((r) => setDeals(r.data)).catch(() => {});
  const loadBookings = () => api.get("/featured/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { if (!isShopper) { load(); loadBookings(); } }, [isShopper]);
  useEffect(() => { if (isShopper && SAVINGS_ENABLED) api.get("/my/savings").then((r) => setSavings(r.data)).catch(() => {}); }, [isShopper, SAVINGS_ENABLED]);
  useEffect(() => {
    if (user) setContact({
      phone: user.phone || "",
      whatsapp: user.whatsapp || "",
      contacts: (user.contacts && user.contacts.length)
        ? user.contacts
        : ((user.phone || user.whatsapp)
            ? [{ label: "Main", phone: user.phone || user.whatsapp || "", isWhatsapp: !!user.whatsapp && !user.phone }]
            : [{ label: "", phone: "", isWhatsapp: false }]),
      contactsPublic: user.contactsPublic !== false,
    });
  }, [user]);
  useEffect(() => { if (!isShopper) api.get("/my/redemptions/summary").then((r) => setRedeemSummary(r.data)).catch(() => {}); }, [isShopper]);
  useEffect(() => { if (!isShopper) api.get("/my/credits").then((r) => setCredits(r.data)).catch(() => {}); }, [isShopper]);
  useEffect(() => { if (isShopper) api.get("/my/codes").then((r) => setMyCodes(r.data)).catch(() => {}); }, [isShopper]);

  const undoCode = async (c) => {
    try {
      await api.delete(`/deals/${c.dealId}/code`);
      setMyCodes((list) => list.filter((x) => x.code !== c.code));
      toast({ title: t("acc.grabUndone"), description: `Code for "${c.dealTitle}" was released.` });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("acc.couldnTUndo"), description: typeof d === "string" ? d : "Please try again." });
    }
  };

  const verifyCode = async (codeArg) => {
    const c = (codeArg ?? redeemCode).trim().toUpperCase();
    if (!c) return;
    try {
      const { data } = await api.post("/redemptions/verify", { code: c });
      setRedeemResult({ ok: true, ...data });
      setRedeemCode("");
      toast({ title: t("acc.codeRedeemed"), description: `${data.dealTitle} — ${data.discount}% off applied.` });
      api.get("/my/redemptions/summary").then((r) => setRedeemSummary(r.data)).catch(() => {});
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not verify code";
      setRedeemResult({ ok: false, msg });
      toast({ title: t("acc.couldnTRedeem"), description: msg });
    }
  };

  // Camera QR scanner for staff — decodes the shopper's QR to the code, then verifies
  useEffect(() => {
    if (!scanning) return;
    let active = true;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!active) return;
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => {
            const c = (decoded || "").trim().toUpperCase();
            setRedeemCode(c);
            setScanning(false);
            verifyCode(c);
          },
          () => {}
        );
      } catch (e) {
        toast({ title: t("acc.cameraUnavailable"), description: t("acc.allowCameraAccessOr") });
        setScanning(false);
      }
    })();
    return () => {
      active = false;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) { s.stop().then(() => s.clear()).catch(() => {}); }
    };
  }, [scanning]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetCur = displayCurrency || "USD";
  const convertBy = (by) => {
    if (!by) return 0;
    let sum = 0;
    for (const [cur, amt] of Object.entries(by)) {
      if (!rates || !rates[cur] || !rates[targetCur]) { sum += Number(amt) || 0; continue; }
      sum += (Number(amt) * rates[targetCur]) / rates[cur];
    }
    return sum;
  };
  const MILESTONES = [50, 100, 250, 500, 1000];

  // Celebrate crossing a new savings milestone (once per level)
  useEffect(() => {
    if (!SAVINGS_ENABLED || !savings) return;
    const total = convertBy(savings.allTime?.byCurrency);
    const current = [...MILESTONES].reverse().find((m) => total >= m);
    if (!current) return;
    const stored = Number(localStorage.getItem("lp-savings-milestone") || 0);
    if (current > stored) {
      localStorage.setItem("lp-savings-milestone", String(current));
      if (stored > 0) toast({ title: `🎉 You've saved over ${symbolFor(targetCur)}${current}!`, description: t("acc.saveMoreWasteLess") });
    }
  }, [savings, rates, displayCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveContact = async () => {
    try {
      const contacts = (contact.contacts || []).filter((c) => (c.phone || "").trim());
      const primary = contacts[0] || {};
      await api.put("/my/business", {
        contacts,
        contactsPublic: contact.contactsPublic,
        phone: primary.isWhatsapp ? "" : (primary.phone || ""),
        whatsapp: contacts.find((c) => c.isWhatsapp)?.phone || "",
      });
      await refresh();
      toast({ title: t("acc.contactInfoSaved"), description: contact.contactsPublic ? "Shoppers can now reach you from your profile." : "Your contact info is hidden from your public profile." });
    } catch { toast({ title: t("acc.couldNotSave"), description: t("acc.pleaseTryAgain") }); }
  };

  const setNumber = (i, patch) => setContact((c) => ({ ...c, contacts: c.contacts.map((n, idx) => idx === i ? { ...n, ...patch } : n) }));
  const addNumber = () => setContact((c) => ({ ...c, contacts: [...c.contacts, { label: "", phone: "", isWhatsapp: false }] }));
  const removeNumber = (i) => setContact((c) => ({ ...c, contacts: c.contacts.filter((_, idx) => idx !== i) }));

  const [uploading, setUploading] = useState(null);
  const handleUpload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/upload", fd);
      await api.put("/my/business", { [field]: data.url });
      await refresh();
      toast({ title: `${field === "logo" ? "Logo" : "Cover photo"} updated` });
    } catch { toast({ title: t("acc.uploadFailed"), description: t("acc.pleaseTryAgain") }); }
    finally { setUploading(null); }
  };

  const remove = async (id, title) => {
    try {
      await api.delete(`/deals/${id}`);
      toast({ title: t("acc.dealRemoved"), description: title });
      load();
    } catch { toast({ title: t("acc.couldNotRemove"), description: t("acc.pleaseTryAgain") }); }
  };

  const totalViews = deals.reduce((s, d) => s + (d.views || 0), 0);
  const totalClaims = deals.reduce((s, d) => s + (d.claims || 0), 0);
  const dealsListRef = useRef(null);
  const scrollToDeals = () => dealsListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const [boostFor, setBoostFor] = useState(null);
  const [boostBusy, setBoostBusy] = useState("");
  const [boostChecking, setBoostChecking] = useState("");
  const [boostExtend, setBoostExtend] = useState(false);
  const [previewDeal, setPreviewDeal] = useState(null);
  const [hideFeatureNudge, setHideFeatureNudge] = useState(() => localStorage.getItem("lp_hide_feature_nudge") === "1");

  const whenLabel = (iso) => {    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  };

  const notifyBoostSlot = async (dealId, category) => {
    try {
      await api.post("/boost/waitlist", null, { params: { deal_id: dealId } });
      toast({ title: t("bst.notifyQueued", { category }) });
    } catch { toast({ title: t("acc.pleaseTryAgain") }); }
  };

  const boost = async (dealId) => {
    setBoostExtend(false);
    setBoostChecking(dealId);
    try { await runBoost(dealId); } finally { setBoostChecking(""); }
  };

  const runBoost = async (dealId) => {
    // Rolling slots: only 3 boosts run at once per category + area, so check before charging anyone.
    try {
      const { data: slot } = await api.get("/boost/slots", { params: { deal_id: dealId } });
      if (slot.myOtherBoost) {
        toast({ title: t("bst.ownBoostLive", { title: slot.myOtherBoost.dealTitle || "Another deal", category: slot.category }) });
        return;
      }
      if (!slot.mine && slot.remaining <= 0) {
        toast({
          title: slot.scope === "radius"
            ? t("bst.slotsFullNear", { limit: slot.limit, category: slot.category, km: slot.radiusKm })
            : t("bst.slotsFull", { limit: slot.limit, category: slot.category, city: slot.city || t("map.thisArea") }),
          description: slot.nextFreeAt ? t("bst.nextFree", { when: whenLabel(slot.nextFreeAt) }) : t("bst.slotsNote", { limit: slot.limit, km: slot.radiusKm }),
          action: slot.waiting ? undefined : (
            <ToastAction altText={t("bst.notifyMe")} data-testid="boost-notify-me" onClick={() => notifyBoostSlot(dealId, slot.category)}>
              {t("bst.notifyMe")}
            </ToastAction>
          ),
        });
        return;
      }
      if (!slot.mine) {
        toast({ title: slot.scope === "radius"
          ? t("bst.slotsLeftNear", { n: slot.remaining, limit: slot.limit, category: slot.category, km: slot.radiusKm })
          : t("bst.slotsLeft", { n: slot.remaining, limit: slot.limit, category: slot.category, city: slot.city || t("map.thisArea") }) });
      }
    } catch { /* if the check fails the server still enforces the cap */ }
    if (credits?.boostPasses > 0) {
      try {
        const { data } = await api.post(`/deals/${dealId}/boost/pass`);
        setCredits(data.credits);
        toast({ title: t("acc.boostedForDays"),
          description: t("acc.usedBoostPass", { n: data.credits.boostPasses }) });
        load();
        return;
      } catch (e) { toast({ title: t("acc.couldNotUseYour"), description: e.response?.data?.detail || "Please try again." }); return; }
    }
    setBoostFor(dealId);   // slots are free: let them choose Boost / Reach / Exclusive
  };

  const buyBoost = async (packageId, code) => {
    setBoostBusy(packageId);
    try { await startCheckout(packageId, { deal_id: boostFor, code }); }
    catch (e) {
      toast({ title: t("acc.couldNotStartCheckout"), description: e.response?.data?.detail || t("acc.pleaseTryAgain") });
      setBoostBusy("");
    }
  };

  const feature = async (dealId, reach, bookingDate) => {
    const pkg = reach === "global" ? "featured_global_24h" : "featured_local_24h";
    try { await startCheckout(pkg, { deal_id: dealId, booking_date: bookingDate || undefined }); }
    catch { toast({ title: t("acc.couldNotStartCheckout"), description: t("acc.pleaseTryAgain") }); }
  };
  const cancelBooking = async (b) => {
    try {
      const { data } = await api.post(`/featured/bookings/${b.id}/cancel`);
      await loadBookings();
      toast({ title: t("acc.bookingCancelled"), description: data?.refunded ? t("acc.bookingRefunded") : undefined });
    } catch (err) {
      toast({ title: t("acc.bookingCancelFailed"), description: err.response?.data?.detail || "" });
    }
  };

  const [duplicating, setDuplicating] = useState(null);
  const duplicate = async (dealId, title) => {
    setDuplicating(dealId);
    try {
      const { data } = await api.post(`/deals/${dealId}/duplicate`);
      toast({ title: t("acc.copyCreated"), description: `Tweak "${title}" before it goes out.` });
      navigate(`/edit-deal/${data.id}`);
    } catch { toast({ title: t("acc.couldNotDuplicate"), description: t("acc.pleaseTryAgain") }); }
    finally { setDuplicating(null); }
  };

  const [renewing, setRenewing] = useState(null);
  const renew = async (dealId) => {
    setRenewing(dealId);
    try {
      const { data } = await api.post(`/deals/${dealId}/renew`);
      toast({ title: data.relisted ? t("acc.renewRelisted") : t("acc.renewDone"),
              description: t("acc.renewNote", { n: data.days }) });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: typeof d === "string" ? d : d?.message || t("acc.pleaseTryAgain") });
    } finally { setRenewing(null); }
  };

  if (isShopper) {    return (
      <div className="space-y-6 lp-fade-up max-w-4xl">
        <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-5">
          <span className="h-20 w-20 rounded-2xl bg-[#16a34a] text-white text-3xl font-extrabold flex items-center justify-center ring-2 ring-[#f5b942]">{user?.name?.[0] || "L"}</span>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold text-foreground">{user?.name}</h1>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="inline-flex items-center gap-1.5 bg-[#e7f6ec] text-[#16a34a] text-xs font-bold px-3 py-1 rounded-full mt-2"><BellRing size={13} /> {t("acc.shopperAccount")}</span>
          </div>
        </div>

        {SAVINGS_ENABLED && (() => {
          const fmt = (n) => `${symbolFor(targetCur)}${Number(n).toLocaleString(undefined, { minimumFractionDigits: n >= 1000 ? 0 : 2, maximumFractionDigits: n >= 1000 ? 0 : 2 })}`;
          const monthTotal = convertBy(savings?.thisMonth?.byCurrency);
          const allTotal = convertBy(savings?.allTime?.byCurrency);
          const count = savings?.allTime?.count || 0;
          const current = [...MILESTONES].reverse().find((m) => allTotal >= m) || 0;
          const next = MILESTONES.find((m) => allTotal < m) || null;
          const progress = next ? Math.min(100, Math.round((allTotal / next) * 100)) : 100;
          return (
            <div data-testid="money-saved-card" className="rounded-2xl p-6 bg-gradient-to-br from-[#0e6b3e] to-[#16a34a] text-white relative overflow-hidden shadow-lg">
              <span className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
              <span className="absolute right-10 bottom-2 h-16 w-16 rounded-full bg-white/10" />
              <div className="relative">
                <p className="flex items-center gap-2 text-white/85 text-sm font-semibold"><PiggyBank size={17} /> {t("acc.verifiedSavingsThisMonth")}</p>
                {count === 0 ? (
                  <>
                    <p className="text-3xl font-extrabold mt-1">{fmt(0)}</p>
                    <p className="text-sm text-white/85 mt-3">{t("acc.redeemADealCode")} <Link to="/browse" className="underline font-semibold">{t("acc.browseDealsArrow")}</Link></p>
                  </>
                ) : (
                  <>
                    <p className="text-4xl font-extrabold mt-1" data-testid="money-saved-month">{fmt(monthTotal)}</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-white/90">
                      <span data-testid="money-saved-alltime">{fmt(allTotal)} saved all-time</span>
                      <span>{count} code{count === 1 ? "" : "s"} redeemed</span>
                    </div>

                    {savings?.streak?.weeks > 0 && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="savings-streak">
                        <span className="inline-flex items-center gap-1.5 bg-white/15 rounded-full pl-2 pr-3 py-1 text-sm font-extrabold">
                          <Flame size={15} className="text-[#ffcf5c]" /> {savings.streak.weeks}-week streak
                        </span>
                        {savings.streak.badge > 0 && (
                          <span className="text-xs bg-[#f5b942] text-[#3b2a00] font-extrabold px-2.5 py-1 rounded-full" data-testid="streak-badge">{savings.streak.badge}-week badge</span>
                        )}
                        {savings.streak.nextTier && (
                          <span className="text-xs text-white/75">redeem again next week to hit {savings.streak.nextTier} weeks</span>
                        )}
                      </div>
                    )}

                    <div className="mt-4">
                      <ShareSavings amountLabel={fmt(allTotal)} className="inline-flex items-center gap-2 bg-white text-[#0e6b3e] hover:bg-white/90 text-sm font-bold px-4 py-2 rounded-xl transition-colors" iconSize={15} />
                    </div>

                    {/* Savings milestones */}
                    <div className="mt-5" data-testid="savings-milestones">
                      <div className="flex flex-wrap items-center gap-2">
                        {MILESTONES.map((m) => {
                          const hit = allTotal >= m;
                          return (
                            <span key={m} data-testid={`milestone-${m}${hit ? "-achieved" : ""}`}
                              className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full transition-colors ${hit ? "bg-[#f5b942] text-[#3b2a00]" : "bg-white/15 text-white/60"}`}>
                              {hit ? <Award size={12} /> : <Lock size={11} />} {symbolFor(targetCur)}{m}
                            </span>
                          );
                        })}
                      </div>
                      {next ? (
                        <>
                          <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full bg-[#f5b942] rounded-full transition-all" style={{ width: `${progress}%` }} data-testid="milestone-progress" />
                          </div>
                          <p className="text-xs text-white/80 mt-1.5">{fmt(next - allTotal)} more to your {symbolFor(targetCur)}{next} badge</p>
                        </>
                      ) : (
                        <p className="text-xs text-[#f5b942] font-bold mt-3 flex items-center gap-1"><Award size={13} /> {t("acc.topSaverEveryBadge")}</p>
                      )}
                    </div>
                  </>
                )}
                <p className="text-xs text-white/70 mt-4 flex items-center gap-1"><Sparkles size={12} /> {t("acc.verifiedRedemptionsOnly")}{savings ? ` · ${t("acc.approxIn", { cur: targetCur })}` : ""}</p>
              </div>
            </div>
          );
        })()}

        {SAVINGS_ENABLED && savings?.recent?.length > 0 && (
          <div data-testid="recent-grabs">
            <h2 className="text-lg font-extrabold text-foreground mb-3">{t("acc.recentRedemptions")}</h2>
            <div className="space-y-3">
              {savings.recent.map((r, i) => (
                <div key={r.dealId + i} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4">
                  <span className="h-11 w-11 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center shrink-0"><Tag size={18} className="text-[#16a34a]" /></span>
                  <div className="flex-1 min-w-0">
                    <Link to={`/deal/${r.dealId}`} className="font-semibold text-foreground truncate block hover:text-[#16a34a] transition-colors">{r.title}</Link>
                    <span className="text-xs text-muted-foreground">{r.business}</span>
                  </div>
                  {r.save > 0 && <span className="text-sm font-bold text-[#16a34a] shrink-0">Saved {symbolFor(r.currency)}{r.save}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-2xl p-5 overflow-hidden" data-testid="display-currency-setting">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-9 w-9 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center shrink-0"><Globe size={18} className="text-[#16a34a]" /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t("acc.showPricesInMy")}</p>
              <p className="text-xs text-muted-foreground">{t("acc.dealsShowAnApproximate")}</p>
            </div>
          </div>
          <select value={displayCurrency} onChange={(e) => setDisplayCurrency(e.target.value)} data-testid="display-currency-select" className="block w-full max-w-full min-w-0 border border-border rounded-xl px-4 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground font-semibold cursor-pointer truncate">
            <option value="">{t("acc.showPricesAsIs")}</option>
            {CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{t("acc.showInCurrency", { code: c.code, label: c.label })}</option>))}
          </select>
        </div>

        <AccountSecurityCard />

        <div className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-bold text-foreground">Signed in as {user?.name || user?.email}</p>
            <p className="text-sm text-muted-foreground mt-0.5">Your grabbed codes stay on your account — sign back in any time.</p>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="account-logout-btn"
            className="inline-flex items-center gap-2 border border-border hover:border-rose-400 hover:text-rose-500 text-foreground text-sm font-bold px-4 py-2.5 rounded-xl transition-colors"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>

        <MyMessagesCard />

        <DeleteAccountCard />

        <TwoFactorCard />

        <PasskeysCard />

        <EmailFrequencyCard />

        <div className="bg-card border border-border rounded-2xl p-5" data-testid="digest-prefs-card">
          <h3 className="font-bold text-foreground flex items-center gap-2 mb-3"><Mail size={18} className="text-[#16a34a]" /> {t("acc.weeklyRoundup")}</h3>
          <button
            type="button"
            onClick={saveDigest}
            data-testid="weekly-digest-toggle"
            className="w-full flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-3 text-left hover:border-[#16a34a]/50 transition-colors"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t("acc.dealsNearYouWeekly")}</p>
              <p className="text-xs text-muted-foreground">{t("acc.fiveClosestDeals")}</p>
            </div>
            <span className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${weeklyDigest ? "bg-[#16a34a]" : "bg-muted"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${weeklyDigest ? "left-[22px]" : "left-0.5"}`} />
            </span>
          </button>
        </div>

        {myCodes.length > 0 && (
          <div data-testid="my-codes">
            <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-extrabold text-foreground">{t("acc.myRedemptionCodes")}</h2><Link to="/my-deals" data-testid="account-my-deals-link" className="text-sm font-semibold text-[#16a34a] hover:underline">See all in My Deals →</Link></div>
            <div className="space-y-3">
              {myCodes.map((c) => (
                <div key={c.code} className={`bg-card border rounded-2xl p-4 flex items-center gap-4 ${c.status === "redeemed" ? "border-[#16a34a]/40 bg-[#e7f6ec]/40 dark:bg-[#16341f]/40" : "border-[#16a34a]"}`} data-testid={`code-${c.code}`}>
                  <div className="bg-white rounded-lg p-1.5 shrink-0"><QRCodeSVG value={c.code} size={56} /></div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/deal/${c.dealId}`} className="font-semibold text-foreground truncate block hover:text-[#16a34a] transition-colors">{c.dealTitle}</Link>
                    <span className="text-xs text-muted-foreground">{c.business} · {c.discount}% off</span>
                    <p className="text-lg font-extrabold tracking-[0.25em] text-foreground mt-0.5">{c.code}</p>
                    {c.status === "redeemed" && (
                      <p className="text-xs font-semibold text-[#16a34a] mt-1" data-testid={`receipt-${c.code}`}>
                        <CheckCircle2 size={12} className="inline mb-0.5 mr-1" />
                        Redeemed{c.redeemedAt ? ` on ${String(c.redeemedAt).slice(0, 10)}` : ""}{c.save ? ` · you saved ${symbolFor(c.currency)}${c.save}` : ""}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full shrink-0 ${c.status === "redeemed" ? "bg-[#16a34a] text-white" : "bg-[#e7f6ec] text-[#16a34a]"}`}>{c.status === "redeemed" ? "Redeemed ✓" : "Show at checkout"}</span>
                  {c.status !== "redeemed" && (
                    <button onClick={() => undoCode(c)} data-testid={`undo-code-${c.code}`} title={t("acc.undoGrabReleaseThis")}
                      className="shrink-0 p-2 rounded-lg text-muted-foreground hover:text-rose-500 hover:bg-secondary transition-colors">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}


        <div>
          <h2 className="text-lg font-extrabold text-foreground mb-3">{t("acc.businessesYouFollow")}</h2>
          {subs.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
              <p className="text-muted-foreground mb-4">{t("acc.youReNotFollowing")}</p>
              <Link to="/businesses" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-6 py-2.5 rounded-xl transition-colors"><Store size={18} /> {t("acc.browseBusinesses")}</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="followed-businesses">
              {subs.map((b) => (
                <div key={b} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4">
                  <span className="h-11 w-11 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Store size={20} className="text-[#16a34a]" /></span>
                  <span className="flex-1 font-bold text-foreground truncate">{b}</span>
                  <button onClick={() => toggleFollow(b)} data-testid={`unfollow-${b.replace(/\s+/g, "-").toLowerCase()}`} className="text-sm font-semibold text-muted-foreground hover:text-rose-500 transition-colors">Unfollow</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lp-fade-up max-w-4xl">
      {previewDeal && (
        <FeaturedPreview deal={previewDeal} localPrice={10} globalPrice={25}
          onClose={() => setPreviewDeal(null)}
          onBuy={(reach, date) => { const id = previewDeal.id; setPreviewDeal(null); feature(id, reach, date); }} />
      )}
      {boostFor && (
        <BoostOptions dealId={boostFor} busy={boostBusy} onPick={buyBoost} extend={boostExtend}
          onClose={() => { setBoostFor(null); setBoostBusy(""); setBoostExtend(false); }} />
      )}
      <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-5">
        <span className="h-20 w-20 rounded-2xl bg-[#16a34a] text-white text-3xl font-extrabold flex items-center justify-center ring-2 ring-[#f5b942]">{user?.businessName?.[0] || "L"}</span>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold text-foreground">{user?.businessName}</h1>
          <p className="text-sm text-muted-foreground">{user?.name} · {user?.email}</p>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin size={15} className="text-[#16a34a]" /> {user?.location}{user?.country ? `, ${user.country}` : ""}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1.5 bg-[#fdf3e0] text-[#b45309] text-xs font-bold px-3 py-1 rounded-full"><Crown size={13} /> {user?.category}</span>
            {user?.currency && <span className="inline-flex items-center gap-1.5 bg-[#e7f6ec] text-[#16a34a] text-xs font-bold px-3 py-1 rounded-full">{symbolFor(user.currency)} {user.currency}</span>}
          </div>
        </div>
        <Link to="/post-deal" className="hidden sm:inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"><PlusCircle size={18} /> {t("acc.postADeal")}</Link>
      </div>

      {/* One long scroll was unusable on a phone: the shop account lives behind sections now. */}
      <div className="sticky top-0 z-30 -mx-4 px-4 py-2 bg-background/95 backdrop-blur border-b border-border sm:mx-0 sm:px-0 sm:border-0 sm:bg-transparent sm:backdrop-blur-none">
        <div className="flex gap-1 overflow-x-auto no-scrollbar bg-secondary rounded-full p-1" data-testid="account-sections">
          {SECTIONS.map(([id, label]) => (
            <button key={id} onClick={() => setSection(id)} data-testid={`account-section-${id}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-all ${section === id ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {section === "deals" && <SecurityNudge onGoToSecurity={() => setSection("account")} />}

      {section === "redeem" && (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="redeem-code-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-bold text-foreground flex items-center gap-2"><QrCode size={18} className="text-[#16a34a]" /> {t("acc.redeemACustomerS")}</h3>
          {redeemSummary && <span className="text-xs font-bold bg-[#e7f6ec] text-[#16a34a] px-3 py-1 rounded-full" data-testid="redemptions-total">{redeemSummary.total} redeemed via LytePress</span>}
        </div>
        <p className="text-xs text-muted-foreground mb-3">{t("acc.whenAShopperShows")}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && verifyCode()} placeholder={t("acc.enterCodeEG")} maxLength={8} data-testid="redeem-code-input" className="w-full sm:flex-1 min-w-0 border border-border rounded-xl px-4 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground font-bold tracking-widest uppercase" />
          <div className="flex gap-2">
            <button onClick={() => setScanning((s) => !s)} data-testid="scan-qr-btn" className={`flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-xl font-semibold transition-colors flex items-center gap-2 ${scanning ? "bg-secondary text-foreground" : "border border-border text-foreground hover:border-[#16a34a]"}`}>{scanning ? <><X size={16} /> Stop</> : <><Camera size={16} className="text-[#16a34a]" /> Scan</>}</button>
            <button onClick={() => verifyCode()} data-testid="redeem-code-btn" className="flex-1 sm:flex-none bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">Redeem</button>
          </div>
        </div>
        {scanning && (
          <div className="mt-3">
            <div id="qr-reader" data-testid="qr-reader" className="rounded-xl overflow-hidden border border-border max-w-xs mx-auto" />
            <p className="text-xs text-muted-foreground text-center mt-2">{t("acc.pointTheCameraAt")}</p>
          </div>
        )}
        {redeemResult && (
          <div className={`mt-3 rounded-xl p-3 text-sm ${redeemResult.ok ? "bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a]" : "bg-[#fde8ee] dark:bg-[#3b0d1c] text-[#e11d48]"}`} data-testid="redeem-result">
            {redeemResult.ok
              ? <span className="font-semibold">✓ {redeemResult.dealTitle} — {redeemResult.discount}% off applied{redeemResult.shopperName ? ` for ${redeemResult.shopperName}` : ""}.</span>
              : <span className="font-semibold">{redeemResult.msg}</span>}
          </div>
        )}
        {redeemSummary?.recent?.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">{t("acc.recentRedemptions")}</p>
            <ul className="space-y-1.5">
              {redeemSummary.recent.map((r, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                  <span className="truncate text-foreground">{r.dealTitle}{r.shopperName ? ` · ${r.shopperName}` : ""}</span>
                  <span className="font-mono shrink-0">{r.code}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      )}

      {section === "redeem" && <RedemptionGoalCard />}

      {section === "account" && (
      <div className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="display-currency-setting">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center"><Globe size={18} className="text-[#16a34a]" /></span>
          <div>
            <p className="text-sm font-semibold text-foreground">{t("acc.showPricesInMy")}</p>
            <p className="text-xs text-muted-foreground">{t("acc.dealsAlsoShowAn")}</p>
          </div>
        </div>
        <select value={displayCurrency} onChange={(e) => setDisplayCurrency(e.target.value)} data-testid="display-currency-select" className="border border-border rounded-xl px-4 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground font-semibold cursor-pointer">
          <option value="">{t("acc.showPricesAsIs")}</option>
          {CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{t("acc.showInCurrency", { code: c.code, label: c.label })}</option>))}
        </select>
      </div>
      )}

      {section === "shop" && (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="business-branding">
        <h3 className="font-bold text-foreground flex items-center gap-2 mb-3"><ImageIcon size={18} className="text-[#16a34a]" /> Branding</h3>
        <p className="text-xs text-muted-foreground -mt-2 mb-3">{t("acc.recommendedLogo")} <strong>400×400 px (square)</strong>, cover <strong>1200×400 px (3:1)</strong>. JPG or PNG, max 8&nbsp;MB. Your cover photo is also used as the background of your card on the Businesses page.</p>
        <div className="relative rounded-xl overflow-hidden border border-border bg-secondary h-32">
          {user?.cover ? <img src={user.cover} alt="Cover" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">{t("acc.noCoverPhotoYet")}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <span className="h-16 w-16 rounded-2xl border border-border bg-[#e7f6ec] dark:bg-[#16341f] overflow-hidden flex items-center justify-center shrink-0">
            {user?.logo ? <img src={user.logo} alt="Logo" className="w-full h-full object-cover" /> : <Store size={22} className="text-[#16a34a]" />}
          </span>
          <label data-testid="upload-logo" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-border rounded-xl px-4 py-2 hover:border-[#16a34a] transition-colors">
            {uploading === "logo" ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} className="text-[#16a34a]" />} Upload logo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("logo", e.target.files?.[0])} />
          </label>
          <label data-testid="upload-cover" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-border rounded-xl px-4 py-2 hover:border-[#16a34a] transition-colors">
            {uploading === "cover" ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} className="text-[#16a34a]" />} Upload cover photo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("cover", e.target.files?.[0])} />
          </label>
        </div>
      </div>
      )}

      {section === "shop" && (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="business-contact-editor">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-bold text-foreground flex items-center gap-2"><Phone size={18} className="text-[#16a34a]" /> {t("acc.contactInfo")}</h3>
          {user?.businessName && <Link to={`/business/${encodeURIComponent(user.businessName)}`} data-testid="view-my-profile" className="text-sm font-semibold text-[#16a34a] hover:underline">View public profile →</Link>}
        </div>

        {/* Master privacy toggle */}
        <button
          type="button"
          onClick={() => setContact((c) => ({ ...c, contactsPublic: !c.contactsPublic }))}
          data-testid="contacts-public-toggle"
          className="w-full flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-3 mb-3 text-left hover:border-[#16a34a]/50 transition-colors"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {contact.contactsPublic ? <Eye size={17} className="text-[#16a34a] shrink-0" /> : <EyeOff size={17} className="text-muted-foreground shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t("acc.showContactInfoOn")}</p>
              <p className="text-xs text-muted-foreground truncate">{contact.contactsPublic ? "Shoppers can see your phone / WhatsApp numbers." : "Your numbers are hidden from shoppers."}</p>
            </div>
          </div>
          <span className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${contact.contactsPublic ? "bg-[#16a34a]" : "bg-muted"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${contact.contactsPublic ? "left-[22px]" : "left-0.5"}`} />
          </span>
        </button>

        {/* Multiple numbers */}
        <div className="space-y-2" data-testid="contact-numbers">
          {contact.contacts.map((n, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`contact-row-${i}`}>
              <input value={n.label} onChange={(e) => setNumber(i, { label: e.target.value })} placeholder={t("acc.labelEGPort")} className="flex-1 min-w-[140px] border border-border rounded-xl px-3 py-2.5 bg-transparent outline-none text-sm text-foreground" data-testid={`contact-label-${i}`} />
              <div className="flex items-center gap-2 border border-border rounded-xl px-3 py-2.5 flex-1 min-w-[160px]">
                {n.isWhatsapp ? <MessageCircle size={16} className="text-[#25D366]" /> : <Phone size={16} className="text-muted-foreground" />}
                <input value={n.phone} onChange={(e) => setNumber(i, { phone: e.target.value })} placeholder="+1 868 555 0100" className="w-full bg-transparent outline-none text-sm text-foreground" data-testid={`contact-phone-${i}`} />
              </div>
              <button type="button" onClick={() => setNumber(i, { isWhatsapp: !n.isWhatsapp })} data-testid={`contact-whatsapp-toggle-${i}`} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2.5 rounded-xl border transition-colors ${n.isWhatsapp ? "bg-[#25D366] text-white border-[#25D366]" : "border-border text-muted-foreground hover:border-[#25D366]"}`}>
                <MessageCircle size={14} /> WhatsApp
              </button>
              {contact.contacts.length > 1 && (
                <button type="button" onClick={() => removeNumber(i)} data-testid={`contact-remove-${i}`} className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:border-rose-300 transition-colors" aria-label={t("acc.removeNumber")}><Trash2 size={15} /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addNumber} data-testid="add-contact-btn" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a] hover:underline"><PlusCircle size={15} /> {t("acc.addAnotherNumber")}</button>

        <div>
          <button onClick={saveContact} data-testid="save-contact-btn" className="mt-3 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{t("acc.saveContactInfo")}</button>
        </div>
      </div>
      )}

      {section === "shop" && <BranchesCard />}

      {section === "shop" && <OpeningHoursCard />}

      {section === "shop" && <ShopLinkCard />}

      {section === "shop" && <ShopPromoCard />}

      {section === "account" && <AccountSecurityCard />}

      {section === "account" && !isShopper && <PlanCard credits={credits} />}

      {section === "account" && !isShopper && <BillingHistory />}

      {section === "account" && <SessionsCard />}

      {section === "account" && <TwoFactorCard />}

      {section === "account" && <PasskeysCard />}

      {section === "account" && <MyMessagesCard />}

      {section === "account" && <EmailFrequencyCard />}

      {section === "account" && <DeleteAccountCard />}

      {section === "deals" && (
      <div className="grid grid-cols-3 gap-4">
        {[{ l: t("acc.activeDeals"), v: deals.length, tid: "shopstat-active-deals", act: scrollToDeals },
          { l: t("acc.totalViews"), v: totalViews, tid: "shopstat-total-views", act: scrollToDeals },
          { l: t("acc.totalClaims"), v: totalClaims, tid: "shopstat-total-claims", act: () => setSection("redeem") }].map((s) => (
          <button key={s.l} type="button" onClick={s.act} data-testid={s.tid}
            className="group bg-card border border-border rounded-2xl p-5 text-center transition-all hover:border-[#16a34a] hover:shadow-md active:scale-[0.98]">
            <p className="text-2xl font-extrabold text-[#16a34a]">{s.v}</p>
            <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-0.5 group-hover:text-[#16a34a] transition-colors">{s.l} <ChevronRight size={12} /></p>
          </button>
        ))}
      </div>
      )}

      {section === "account" && <AppealsCard />}

      {section === "account" && <VerificationCard />}

      {section === "deals" && credits && (
        <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm" data-testid="plan-strip">
          <span className="font-bold text-foreground">{t("acc.planLabelPlan", { plan: credits.planLabel })}</span>
          <span className={credits.monthlyAllowance != null && credits.dealsThisMonth >= credits.monthlyAllowance ? "text-rose-600 font-semibold" : "text-muted-foreground"} data-testid="plan-deal-usage">
            {t("pc.nOfMThisMonth", { n: credits.dealsThisMonth, m: credits.monthlyAllowance == null ? "∞" : credits.monthlyAllowance })}
          </span>
          {credits.dealCredits > 0 && <span className="text-[#16a34a] font-semibold" data-testid="plan-deal-credits">{t("pc.nCreditsHeld", { n: credits.dealCredits })}</span>}
          {credits.boostPasses > 0 && <span className="text-[#16a34a] font-semibold" data-testid="plan-boost-passes">{t("sr.passesHeld", { n: credits.boostPasses })}</span>}
          {credits.plan === "business_plus" ? (
            <span className="ml-auto"><ChainEnquiry dealLimit={credits.planMonthlyAllowance} branchLimit={25} /></span>
          ) : credits.monthlyAllowance != null && (
            <Link to="/pricing" data-testid="plan-upgrade-link" className="text-[#16a34a] font-semibold hover:underline ml-auto">{t("acc.upgradeForMore")}</Link>
          )}
        </div>
      )}

      {section === "deals" && user?.role === "business" && <SlotRadar />}

      {section === "deals" && (
      <div ref={dealsListRef}>
        {user?.role === "business" && bookings.length > 0 && (
          <div className="mb-5 rounded-2xl border border-border bg-card p-4" data-testid="my-bookings-panel">
            <h3 className="text-sm font-extrabold text-foreground mb-3">{t("acc.bookingsTitle")}</h3>
            <div className="space-y-2">
              {bookings.map((b) => {
                const bd = deals.find((d) => d.id === b.dealId);
                const label = new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
                return (
                  <div key={b.id} data-testid={`booking-row-${b.id}`} className="flex items-center gap-3 rounded-xl border border-border/70 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">{bd?.title || b.category}</p>
                      <p className="text-xs text-muted-foreground">
                        {label} · {b.reach === "global" ? t("fp.reachGlobal") : t("fp.reachLocal")}
                        {b.status === "active" && <span className="ml-2 text-[#16a34a] font-semibold">{t("acc.bookingLive")}</span>}
                      </p>
                    </div>
                    {b.status === "booked" && (
                      <button onClick={() => cancelBooking(b)} data-testid={`booking-cancel-${b.id}`}
                        className="shrink-0 text-xs font-semibold text-red-600 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950 px-3 py-1.5 rounded-lg transition-colors">
                        {t("acc.bookingCancel")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <h2 className="text-lg font-extrabold text-foreground mb-3">{t("acc.myDeals")}</h2>
        {user?.role === "business" && (() => {
          const anyFeatured = deals.some((d) => new Date(d.featuredUntil || 0) > new Date());
          const target = deals.find((d) => !(new Date(d.featuredUntil || 0) > new Date()) && !d.hidden && !(d.publishAt && new Date(d.publishAt) > new Date()));
          if (anyFeatured || !target || hideFeatureNudge) return null;
          return (
            <div data-testid="first-sale-nudge" className="relative mb-4 rounded-2xl border border-[#f5b942] bg-gradient-to-br from-[#fdf6e3] to-[#fbeecb] dark:from-[#3a2f10] dark:to-[#2a2109] p-5">
              <button onClick={() => { localStorage.setItem("lp_hide_feature_nudge", "1"); setHideFeatureNudge(true); }} data-testid="first-sale-nudge-dismiss" className="absolute top-3 right-3 text-[#b8860b] hover:opacity-70 text-lg leading-none">✕</button>
              <div className="flex items-start gap-3 pr-6">
                <span className="text-2xl leading-none">★</span>
                <div className="min-w-0">
                  <p className="font-extrabold text-foreground">{t("acc.featureNudgeTitle")}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{t("acc.featureNudgeBody")}</p>
                  <button onClick={() => setPreviewDeal(target)} data-testid="first-sale-nudge-cta" className="mt-3 inline-flex items-center gap-1.5 bg-[#0f2a1a] text-[#f5b942] font-bold text-sm px-4 py-2 rounded-xl hover:opacity-90 transition-opacity">★ {t("acc.featureNudgeCta")}</button>
                </div>
              </div>
            </div>
          );
        })()}
        {deals.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 text-center" data-testid="get-listed-onboarding">
            <span className="inline-flex h-12 w-12 rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] items-center justify-center mb-3"><Store size={22} className="text-[#16a34a]" /></span>
            <p className="text-lg font-extrabold text-foreground">{t("acc.getListedTitle")}</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md mx-auto">{t("acc.getListedBody")}</p>
            <Link to="/post-deal" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-6 py-2.5 rounded-xl transition-colors"><PlusCircle size={18} /> {t("acc.postYourFirstDeal")}</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {deals.map((d) => (
              <div key={d.id} className="bg-card border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <Link to={`/deal/${d.id}`} className="shrink-0"><img src={thumb(d.image, 96)} alt={d.title} loading="lazy" decoding="async" className="h-16 w-16 rounded-xl object-cover" /></Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/deal/${d.id}`} className="min-w-0"><h3 className="font-bold text-foreground hover:text-[#16a34a] truncate">{d.title}</h3></Link>
                      {d.pendingApproval ? (
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full shrink-0" data-testid={`status-pending-${d.id}`}>{t("acc.pendingReview")}</span>
                      ) : d.hidden ? (
                        <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full shrink-0" data-testid={`status-archived-${d.id}`}>{t("acc.archived")}</span>
                      ) : d.publishAt && new Date(d.publishAt) > new Date() ? (
                        <span className="text-[10px] font-bold bg-[#e6effd] text-[#2563eb] px-2 py-0.5 rounded-full shrink-0" data-testid={`status-scheduled-${d.id}`}>Scheduled</span>
                      ) : d.expiresAt && new Date(d.expiresAt) <= new Date() ? (
                        <span className="text-[10px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full shrink-0" data-testid={`status-expired-${d.id}`}>Expired</span>
                      ) : (
                        <span className="text-[10px] font-bold bg-[#e7f6ec] text-[#16a34a] px-2 py-0.5 rounded-full shrink-0" data-testid={`status-live-${d.id}`}>Live</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{d.category} · {d.location}</p>
                    {d.hidden && (
                      <div className="mt-1.5 rounded-xl border border-rose-300/70 bg-rose-50/70 dark:bg-rose-950/20 px-3 py-2" data-testid={`archived-note-${d.id}`}>
                        <p className="text-xs text-rose-700 dark:text-rose-300">
                          <span className="font-bold">{t("acc.archivedWhy")}</span>{" "}
                          {d.hiddenReason || t("acc.archivedNoReason")}
                        </p>
                        <button onClick={() => { setSection("account"); setTimeout(() => document.querySelector('[data-testid="appeals-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60); }}
                          data-testid={`explain-btn-${d.id}`} className="text-xs font-bold text-rose-700 dark:text-rose-300 hover:underline mt-1">
                          {t("acc.explainAppeal")}
                        </button>
                      </div>
                    )}
                    {d.publishAt && new Date(d.publishAt) > new Date() && (
                      <p className="text-xs text-[#2563eb] mt-0.5">{t("acc.goesLive", { when: new Date(d.publishAt).toLocaleString() })}{d.scheduleTimezone ? ` · ${d.scheduleTimezone.replace(/_/g, " ")}` : ""}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye size={13} /> {t("an.nViews", { n: d.views })}</span>
                      <span className="flex items-center gap-1"><Tag size={13} /> {t("fn.nGrabs", { n: d.claims })}</span>
                      <span data-testid={`redeemed-${d.id}`} title={t("fn.redeemedNote")} className={`flex items-center gap-1 font-semibold ${d.redeemed > 0 ? "text-[#16a34a]" : "text-muted-foreground"}`}>
                        <CheckCircle2 size={13} /> {t("fn.nRedeemed", { n: d.redeemed || 0 })}
                      </span>
                      <span data-testid={`directions-${d.id}`} title={t("fn.directionsNote")} className={`flex items-center gap-1 font-semibold ${d.directions > 0 ? "text-[#2563eb]" : "text-muted-foreground"}`}>
                        <Navigation size={13} /> {t("fn.nDirections", { n: d.directions || 0 })}
                      </span>
                      <span className="text-[#16a34a] font-semibold">{d.discount}% OFF</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap">
                  {new Date(d.boostedUntil || 0) > new Date() ? (
                    <>
                      <span className="text-xs font-bold text-[#b8860b] bg-[#fdf6e3] dark:bg-[#3a2f10] px-2.5 py-1.5 rounded-lg flex items-center gap-1 shrink-0" data-testid={`boosted-badge-${d.id}`}><Zap size={13} /> Boosted</span>
                      <button onClick={() => { setBoostExtend(true); setBoostFor(d.id); }} data-testid={`extend-boost-btn-${d.id}`}
                        title={`Boosted until ${new Date(d.boostedUntil).toLocaleDateString()} — add more days on the end`}
                        className="text-xs font-semibold text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">
                        <CalendarClock size={13} /> Extend
                      </button>
                    </>
                  ) : (
                    <button onClick={() => boost(d.id)} disabled={boostChecking === d.id} data-testid={`boost-btn-${d.id}`} className="text-xs font-semibold text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] disabled:opacity-60 px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">
                      {boostChecking === d.id ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} Boost
                    </button>
                  )}
                  {new Date(d.featuredUntil || 0) > new Date() ? (
                    <span className="text-xs font-bold text-[#f5b942] bg-[#0f2a1a] px-2.5 py-1.5 rounded-lg flex items-center gap-1 shrink-0" data-testid={`featured-badge-${d.id}`}>★ Featured</span>
                  ) : (
                    <>
                      <button onClick={() => setPreviewDeal(d)} data-testid={`feature-preview-btn-${d.id}`} title={t("fp.title")}
                        className="text-xs font-semibold text-muted-foreground border border-border hover:bg-secondary px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">
                        <Eye size={13} /> Preview
                      </button>
                      <button onClick={() => setPreviewDeal(d)} data-testid={`feature-btn-${d.id}`} title="Featured on the home page — one spot per category (from $10 / 24 hours)" className="text-xs font-semibold text-[#b8860b] border border-[#f5b942] hover:bg-[#fdf6e3] dark:hover:bg-[#3a2f10] px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">★ Feature</button>
                    </>
                  )}
                  {(() => {
                    const ends = d.expiresAt ? new Date(d.expiresAt) : null;
                    const showRenew = !d.hidden && ends && ends.getTime() - Date.now() < 72 * 3600 * 1000;
                    return showRenew ? (
                      <button onClick={() => renew(d.id)} disabled={renewing === d.id} data-testid={`renew-btn-${d.id}`}
                        title={t("acc.renewTitle")} className="text-xs font-bold text-white bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">
                        {renewing === d.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {t("acc.renew")}
                      </button>
                    ) : null;
                  })()}
                  <button onClick={() => duplicate(d.id, d.title)} disabled={duplicating === d.id} data-testid={`duplicate-btn-${d.id}`} title={t("acc.duplicateThisDealAnd")} className="text-xs font-semibold text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] disabled:opacity-60 px-3 py-1.5 rounded-lg flex items-center gap-1 shrink-0 transition-colors">
                    {duplicating === d.id ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Duplicate
                  </button>
                  <button onClick={() => remove(d.id, d.title)} className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:border-rose-300 transition-colors shrink-0" aria-label="Delete"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Account;
